import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import ThemePreviewFrame from '@/components/feature/ThemePreviewFrame';
import { buildThemePreviewDocument } from '@/lib/themePreviewRender';

interface ThemePreviewModalProps {
  themeId: number;
  title: string;
  stylesheet: string;
  onClose: () => void;
}

interface PageEntry {
  key: string;
  title: string;
}

/** Pages de structure à afficher seules (sans header/footer autour). */
const LAYOUT_PAGES = new Set(['head', 'header', 'navmenu', 'footer', 'head_tags']);

/**
 * Aperçu public réel d'un thème avec NAVIGATION MULTI-PAGES :
 * une liste latérale permet de parcourir toutes les pages du thème
 * (accueil, produits, contact…), avec boutons précédent/suivant,
 * rechargement et sélecteur de largeur d'écran.
 */
export default function ThemePreviewModal({ themeId, title, stylesheet, onClose }: ThemePreviewModalProps) {
  const [pages, setPages] = useState<Record<string, string>>({});
  const [pageList, setPageList] = useState<PageEntry[]>([]);
  const [activeKey, setActiveKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error: err } = await supabase
          .from('sitewebthemepage')
          .select('page_key, title, content')
          .eq('idtheme', themeId);
        if (err) throw new Error(err.message);
        const map: Record<string, string> = {};
        const list: PageEntry[] = [];
        (data || []).forEach((row: { page_key: string; title: string | null; content: string | null }) => {
          if (row.content && row.content.trim()) {
            map[row.page_key] = row.content;
            list.push({ key: row.page_key, title: row.title || row.page_key });
          }
        });
        if (active) {
          setPages(map);
          setPageList(list);
          setActiveKey((prev) => (prev && map[prev] !== undefined ? prev : (map.home !== undefined ? 'home' : (list[0]?.key || ''))));
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [themeId, reloadKey]);

  // Reconstruit le document uniquement quand la page active change (perf).
  const documentHtml = useMemo(() => {
    if (!activeKey || pages[activeKey] === undefined) return '';
    if (LAYOUT_PAGES.has(activeKey)) {
      return buildThemePreviewDocument({ stylesheet: stylesheet || '', pages: { home: pages[activeKey] }, homeKey: 'home', title: `Aperçu — ${title}` });
    }
    return buildThemePreviewDocument({ stylesheet: stylesheet || '', pages, homeKey: activeKey, title: `Aperçu — ${title}` });
  }, [activeKey, pages, stylesheet, title]);

  const currentIndex = pageList.findIndex((p) => p.key === activeKey);
  const goPrev = () => { if (currentIndex > 0) setActiveKey(pageList[currentIndex - 1].key); };
  const goNext = () => { if (currentIndex >= 0 && currentIndex < pageList.length - 1) setActiveKey(pageList[currentIndex + 1].key); };

  const deviceWidth = device === 'desktop' ? '100%' : device === 'tablet' ? '768px' : '390px';

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
        <div className="w-full max-w-6xl h-[90vh] bg-background-50 rounded-lg border border-background-200/70 flex flex-col overflow-hidden shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-4 h-12 border-b border-background-200/70 flex-shrink-0 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-eye-line text-accent-600"></i>
              <span className="text-sm font-semibold text-foreground-900 truncate">Aperçu public — {title}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {pageList.length > 1 && (
                <div className="hidden sm:flex items-center gap-1">
                  <button onClick={goPrev} disabled={currentIndex <= 0} title="Page précédente"
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                    <i className="ri-arrow-left-s-line text-foreground-500"></i>
                  </button>
                  <span className="text-[10px] text-foreground-400 tabular-nums min-w-[42px] text-center">
                    {currentIndex >= 0 ? `${currentIndex + 1}/${pageList.length}` : '—'}
                  </span>
                  <button onClick={goNext} disabled={currentIndex >= pageList.length - 1} title="Page suivante"
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                    <i className="ri-arrow-right-s-line text-foreground-500"></i>
                  </button>
                </div>
              )}
              {/* Device switcher */}
              <div className="flex items-center bg-background-100 rounded-full p-0.5">
                {([
                  { key: 'desktop', icon: 'ri-computer-line' },
                  { key: 'tablet', icon: 'ri-tablet-line' },
                  { key: 'mobile', icon: 'ri-smartphone-line' },
                ] as const).map((d) => (
                  <button
                    key={d.key}
                    onClick={() => setDevice(d.key)}
                    title={d.key}
                    className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                      device === d.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-400 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${d.icon} text-xs`}></i>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                title="Rafraîchir"
              >
                <i className="ri-refresh-line text-xs text-foreground-500"></i>
              </button>
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden flex">
            {loading ? (
              <div className="h-full w-full flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
              </div>
            ) : error ? (
              <div className="h-full w-full flex items-center justify-center">
                <div className="text-center max-w-sm px-4">
                  <i className="ri-error-warning-line text-3xl text-red-500 mb-2 block"></i>
                  <p className="text-sm text-foreground-700 mb-3">{error}</p>
                  <button
                    onClick={() => setReloadKey((k) => k + 1)}
                    className="px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-medium cursor-pointer hover:bg-foreground-800 transition-colors"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            ) : pageList.length === 0 ? (
              <div className="h-full w-full flex items-center justify-center">
                <div className="text-center max-w-sm px-4">
                  <i className="ri-file-list-3-line text-3xl text-foreground-300 mb-2 block"></i>
                  <p className="text-sm text-foreground-600">Ce thème n&apos;a encore aucune page avec du contenu.</p>
                  <p className="text-xs text-foreground-400 mt-1">Génère ou édite le thème puis reviens pour l&apos;aperçu.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Navigation multi-pages */}
                <div className="w-[130px] sm:w-[200px] border-r border-background-200/70 bg-background-100/50 overflow-y-auto flex-shrink-0">
                  <div className="p-2">
                    <div className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold px-2 py-1.5">
                      Pages ({pageList.length})
                    </div>
                    {pageList.map((p) => {
                      const isSelected = p.key === activeKey;
                      return (
                        <button
                          key={p.key}
                          onClick={() => setActiveKey(p.key)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors cursor-pointer mb-0.5 ${
                            isSelected ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                          }`}
                          title={p.title}
                        >
                          <i className={`${LAYOUT_PAGES.has(p.key) ? 'ri-layout-line' : 'ri-file-line'} text-sm flex-shrink-0`}></i>
                          <span className="truncate text-left">{p.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Iframe */}
                <div className="flex-1 bg-background-100 overflow-auto flex justify-center">
                  <div className="bg-background-50 h-full transition-all" style={{ width: deviceWidth, maxWidth: '100%' }}>
                    <ThemePreviewFrame
                      key={`${activeKey}-${device}-${reloadKey}`}
                      documentHtml={documentHtml}
                      title={`Aperçu — ${title}`}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}